import {useEffect, useMemo, useState} from 'react';

import {HappinessItem, Item} from '../features/company/culture/surveys/types/SurveyQuestionnaireTypes';
import {Answer} from '../lib/api/RequestBodies/features/company/Questionnaires/QuestionnaireRequestBodies';

export const useQuestionnaireNavigation = <T extends Item | HappinessItem>(questions: T[][]) => {
  const [questionSections, setQuestionSections] = useState<T[][]>([]);
  const [answerSections, setAnswerSections] = useState<Array<Answer[]>>([]);
  const [sectionIndex, setSectionIndex] = useState(0);

  useEffect(() => {
    if (questions.length) {
      setQuestionSections(questions);
      // Preserve existing answers when questions grow; only initialise new sections.
      setAnswerSections((prev) => {
        if (prev.length >= questions.length) return prev;
        const next = [...prev];
        // I don't know why the model did it this way, it says its defensive code when multiple questions get added,
        // but we are only adding one at a time. I'd just call next.push([]) once instead of looping the difference.
        // but since I don't know react and the model still defends this implementation, I'll leave it as is.
        for (let i = prev.length; i < questions.length; i++) next.push([]);
        return next;
      });
    }
  }, [questions]);

  const onSectionIndexChange = (index: number) => {
    setSectionIndex(index);
  };

  const onAnswer = (event: any) => {
    const resourceId = event.target.name as string;
    const value = event.target.value as number;
    const currentSection = answerSections[sectionIndex];
    const newAnswer = {
      resourceId,
      value,
    };

    // Filters out old answer in case it exists.
    const updatedSection = currentSection.filter((a) => a.resourceId !== newAnswer.resourceId);
    updatedSection.push(newAnswer);
    const answers = answerSections;
    answers[sectionIndex] = updatedSection;
    setAnswerSections([...answers]);
  };

  const sections = useMemo(() => {
    return questionSections.map((q, i) => ({
      isFinished: q.length > 0 && answerSections[i].length === q.length,
      isLocked: q.length === 0,
    }));
  }, [questionSections, answerSections]);

  return {
    onAnswer,
    onSectionIndexChange,
    questionSections,
    answerSections,
    sections,
    sectionIndex,
  };
};
